/**
 * Inflate Action - Inflate the rubber boat with the hand pump
 *
 * The inflatable boat must be deflated and the player must have the pump.
 * Per Zork instructions: "To Inflate: Apply pump to valve."
 *
 * Pattern: "inflate boat", "inflate boat with pump", "pump boat"
 */

import { Action, ActionContext, ValidationResult } from '@sharpee/stdlib';
import { ISemanticEvent } from '@sharpee/core';
import { IdentityTrait, IFEntity, VehicleTrait, EnterableTrait, TraitType } from '@sharpee/world-model';
import { InflatableTrait } from '../../traits';
import { INFLATE_ACTION_ID, InflateMessages } from './types';

/**
 * Check if entity is the inflatable boat (deflated or inflated)
 */
function isBoat(entity: IFEntity): boolean {
  const identity = entity.get(IdentityTrait);
  if (!identity) return false;

  const name = identity.name?.toLowerCase() || '';
  const aliases = identity.aliases || [];

  // Check name or aliases for boat-related terms
  const boatTerms = ['boat', 'raft', 'plastic', 'pile'];
  return boatTerms.some(term => name.includes(term)) ||
    aliases.some((a: string) => boatTerms.some(term => a.toLowerCase().includes(term)));
}

/**
 * Check if entity is the hand pump
 */
function isPump(entity: IFEntity): boolean {
  const identity = entity.get(IdentityTrait);
  if (!identity) return false;

  const name = identity.name?.toLowerCase() || '';
  const aliases = identity.aliases || [];

  return name.includes('pump') ||
    aliases.some((a: string) => a.toLowerCase().includes('pump'));
}

/**
 * Find pump in player's inventory
 */
function findPump(context: ActionContext): IFEntity | undefined {
  const inventory = context.world.getContents(context.player.id);
  return inventory.find(item => isPump(item));
}

/**
 * Find boat in current location or inventory
 */
function findBoat(context: ActionContext): IFEntity | undefined {
  const { world, player } = context;
  const playerLocation = world.getLocation(player.id);

  // Check inventory first
  const inventory = world.getContents(player.id);
  const boatInInventory = inventory.find(item => isBoat(item));
  if (boatInInventory) return boatInInventory;

  // Check room
  if (playerLocation) {
    const roomContents = world.getContents(playerLocation);
    const boatInRoom = roomContents.find(item => isBoat(item));
    if (boatInRoom) return boatInRoom;
  }

  return undefined;
}

export const inflateAction: Action = {
  id: INFLATE_ACTION_ID,
  group: 'manipulation',

  validate(context: ActionContext): ValidationResult {
    // Find the boat
    const boat = findBoat(context);
    if (!boat) {
      return {
        valid: false,
        error: InflateMessages.NOT_INFLATABLE
      };
    }

    // Check if boat is already inflated
    const inflatableTrait = boat.get(InflatableTrait);
    if (inflatableTrait?.isInflated) {
      return {
        valid: false,
        error: InflateMessages.ALREADY_INFLATED
      };
    }

    // Must have pump
    const pump = findPump(context);
    if (!pump) {
      return {
        valid: false,
        error: InflateMessages.NO_PUMP
      };
    }

    context.sharedData.boat = boat;
    context.sharedData.pump = pump;
    return { valid: true };
  },

  execute(context: ActionContext): void {
    const { sharedData } = context;
    const boat = sharedData.boat as IFEntity;

    // Inflate the boat
    const inflatableTrait = boat.get(InflatableTrait);
    if (inflatableTrait) {
      inflatableTrait.isInflated = true;
    }

    // Update name to reflect inflated state (name mutation stays — ISSUE-070 scope)
    const identity = boat.get(IdentityTrait);
    if (identity) {
      identity.name = 'magic boat';
      identity.article = 'a';
      // Description sourced from InflatableTrait field (ISSUE-070)
      if (inflatableTrait?.inflatedDescription) {
        identity.description = inflatableTrait.inflatedDescription;
      }
    }

    // Also update displayName attribute (used by entity.name getter)
    boat.attributes.displayName = 'magic boat';

    // Add EnterableTrait so player can BOARD/ENTER the boat
    if (!boat.has(TraitType.ENTERABLE)) {
      boat.add(new EnterableTrait({ preposition: 'in' }));
    }

    // Add VehicleTrait for watercraft behavior
    // blocksWalkingMovement: false allows GO commands while in boat
    if (!boat.has(TraitType.VEHICLE)) {
      boat.add(new VehicleTrait({
        vehicleType: 'watercraft',
        blocksWalkingMovement: false,
        requiresExitBeforeLeaving: false,
        isOperational: true,
        transparent: true
      }));
    }
  },

  blocked(context: ActionContext, result: ValidationResult): ISemanticEvent[] {
    return [context.event('dungeo.event.inflate_blocked', {
      messageId: result.error || InflateMessages.NOT_INFLATABLE
    })];
  },

  report(context: ActionContext): ISemanticEvent[] {
    return [context.event('game.message', {
      messageId: InflateMessages.SUCCESS
    })];
  }
};
